import React from 'react'
import noop from 'lib/noop'
import cn from 'clsx'
import styled from 'styled-components'
import Caret from '../../Common/Caret'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'

const PositionedCaret = styled(Caret)`
  position: absolute;
  border-top-color: ${props => props.theme.palette.neutral.eight};
  border-width: ${rem(13)};

  &.position-below {
    top: 1px;
    transform: translateX(50%) translateY(-100%) rotate(180deg);
  }

  &.position-above {
    bottom: 1px;
    transform: translateX(50%) translateY(100%);
  }

  &::before {
    bottom: 1px;
    left: ${rem(-12)};
    border-width: ${rem(12)};
  }

  &.align-left {
    left: 20%;
  }

  &.align-right {
    left: 80%;
  }

  &.align-center {
    left: 50%;
  }

  &.white {
    border-top-color: ${props => props.theme.palette.neutral.eight};
  }

  &.black {
    border-top-color: ${props => props.theme.palette.neutral.one};
  }

`

const DropdownWrapper = styled.div`
  position: absolute;
  z-index: ${zIndex.menu};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  visibility: hidden;

  &.visible {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }

  &.position-below {
    bottom: -${rem(8)};
    transform: translate3d(0, 100%, 0);

    &.caret-none {
      bottom: -${rem(8)};
    }
  }

  &.position-above {
    top: -${rem(8)};
    transform: translate3d(0, -100%, 0);

    &.caret-none {
      top: -${rem(8)};
    }
  }

  &.align-left {
    left: 50%;

    &.position-below {
      transform: translate3d(-20%, 100%, 0);
    }

    &.position-above {
      transform: translate3d(-20%, -100%, 0);
    }
  }

  &.align-right {
    left: 50%;

    &.position-below {
      transform: translate3d(-80%, 100%, 0);
    }

    &.position-above {
      transform: translate3d(-80%, -100%, 0);
    }
  }

  &.align-center {
    left: 50%;

    &.position-below {
      transform: translate3d(-50%, 100%, 0);
    }

    &.position-above {
      transform: translate3d(-50%, -100%, 0);
    }
  }
`

export type DropdownAlignments = 'left' | 'center' | 'right' | 'none';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: (e: MouseEvent) => void;
  open?: boolean;
  caretClass?: string;
  triggerRef?: React.RefObject<HTMLElement>;
  asComponent?: keyof JSX.IntrinsicElements;
  alignment?: DropdownAlignments;
  position?: 'above' | 'below';
  caretAlignment?: DropdownAlignments;
  caretType?: 'white' | 'black' | 'none';
}

/**
 * Generic "dropdown" container that fires an on close when something other than
 * itself is clicked.
 */
class Dropdown extends React.Component<React.PropsWithChildren<Props>> {
  dropdownRef = React.createRef<HTMLDivElement>()

  static defaultProps = {
    onClose: noop,
    alignment: 'center',
    caretType: 'none',
    position: 'below',
  }

  componentDidMount() {
    window.addEventListener('click', this.closeOnClick, true)
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.closeOnClick, true)
  }

  shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
    if (!this.props.open && !nextProps.open) {
      // if we're already closed and we're still closed on next update
      // there's no chance we're showing our elements - no need to update
      return false
    }
    return true
  }

  closeOnClick = (e) => {
    const { open, triggerRef, onClose } = this.props
    if (
      open &&
        // don't close if they've clicked inside our drop down
        !(
          this.dropdownRef.current?.contains(e.target as HTMLElement) ||
          // also don't close if they've clicked on our trigger element
          (triggerRef?.current?.contains(e.target as HTMLElement))
        )
    ) {
      onClose?.(e)
    }
  }

  render() {
    const {
      onClose,
      asComponent,
      className,
      open,
      caretClass,
      caretAlignment,
      children,
      alignment,
      caretType,
      position,
      ...rest
    } = this.props

    return (
      <DropdownWrapper
        {...rest}
        as={asComponent}
        ref={this.dropdownRef}
        className={cn(
          className,
          `align-${alignment}`,
          `position-${position}`,
          `caret-${caretType}`,
          { visible: open })}
      >
        {caretType !== 'none' && <PositionedCaret className={cn(
          caretClass,
          caretType,
          `align-${caretAlignment ?? alignment}`,
          `position-${position}`,
        )} />}
        {children}
      </DropdownWrapper>
    )
  }
}

export default Dropdown
